/**
 * Control Entity - RTK Query API
 * API endpoints for Control domain using RTK Query with Django REST Framework
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
import { fetchAllPages } from '../../../shared/api/paginate';
import { archiveArgs } from '../../../shared/api/archiveArgs';

const normalizeListResponse = (response) => ({
  data: response?.results || response || [],
  total: response?.count || response?.length || 0,
});

const toFormData = (data) => {
  const form = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    form.append(key, value);
  });
  return form;
};

export const controlApiTestUtils = { toFormData };

const filenameFromDisposition = (header) => {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || null;
};

const saveBlob = (blob, filename) => {
  if (typeof window === 'undefined' || !window.URL?.createObjectURL) return;
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
};

// CTL-20: control tests are owned by apps/compliance (/api/v1/compliance/
// control-tests/) and the compliance control-test screen uses the compliance
// entity's hooks. The duplicate getControlTests/createControlTest/
// updateControlTest endpoints that used to live here had no callers and were
// removed (see __tests__/controlApi.test.js).

const toControlStatus = (status) => {
  // CTL-19: Control.STATUS_CHOICES (models.py:66-71) has no 'archived'
  // value -- the terminal/retired status is 'deprecated'. Mapping to
  // 'archived' meant any status: 'Archived' filter silently matched
  // nothing on the backend.
  const statusMap = {
    Active: 'active',
    Draft: 'draft',
    Archived: 'deprecated',
  };

  return statusMap[status] || status;
};

// CTL-06: shared filter->query-param mapping so the paginated `getControls`
// list and the fetch-all `getAllControls` (matrix/KPI/export callers) can
// never drift apart on what a given filter set means.
export const buildControlListParams = (filters = {}) => {
  const params = {};
  if (filters.status && filters.status !== 'all') {
    params.status = toControlStatus(filters.status);
  }
  // CTL-07: real callers (FilterPanel/controls-library-management) keep this
  // under `control_type`, matching the backend param name -- `filters.type`
  // is never set by anything, so the Control Type filter previously no-oped.
  if (filters.control_type) {
    params.control_type = filters.control_type;
  }
  if (filters.category) {
    params.category = filters.category;
  }
  if (filters.effectiveness) {
    params.effectiveness = filters.effectiveness;
  }
  if (filters.search) {
    params.search = filters.search;
  }
  return params;
};

// CTL-06: page through every result rather than the first page. Callers
// that need the *whole* tenant list (matrix, KPI dashboards, CSV export) --
// as opposed to a paged table -- use this instead of `getControls`, which
// stays capped at one page for actual paginated list views.
//
// Ordering is pinned to the (unique) `id` rather than left at the model's
// default Meta.ordering (Control: reference/name; ControlAssignment:
// department/control), which is not unique -- OFFSET pagination over a
// non-unique order is not guaranteed stable across the repeated queries
// fetchAllPages issues, so ties could duplicate into or drop out of
// adjacent pages. Display order doesn't matter for these callers (matrix
// cells and KPI stats are keyed/grouped, not row-order-dependent).
export const fetchAllControls = (baseQuery, filters = {}) =>
  fetchAllPages(
    baseQuery,
    '/api/v1/controls/',
    { ordering: 'id', ...buildControlListParams(filters) },
    { pageSize: 500, maxPages: 20 }
  );

export const fetchAllControlAssignments = (baseQuery, { department, control, status } = {}) => {
  const params = { ordering: 'id' };
  if (department) params.department = department;
  if (control) params.control = control;
  if (status) params.status = status;
  return fetchAllPages(baseQuery, '/api/v1/controls/assignments/', params, {
    pageSize: 500,
    maxPages: 20,
  });
};

export const controlApi = createApi({
  reducerPath: 'controlApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Control',
    'ControlCategory',
    'ControlStats',
    'ControlAssignment',
    'ControlEvidence',
    'ControlDeficiency',
    'ControlSelfAssessment',
    // CTL-11
    'ControlFrameworkMapping',
    'FrameworkCoverage',
  ],
  endpoints: (builder) => ({
    // =========================================================================
    // Controls
    // =========================================================================
    getControls: builder.query({
      query: ({ filters = {}, page = 1, limit = 20, ordering = '-created_at' } = {}) => {
        const params = new URLSearchParams();
        params.set('page', page.toString());
        params.set('page_size', limit.toString());
        params.set('ordering', ordering);

        Object.entries(buildControlListParams(filters)).forEach(([key, value]) => {
          params.set(key, value);
        });

        return {
          url: `/api/v1/controls/?${params.toString()}`,
        };
      },
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Control', id })),
              { type: 'Control', id: 'LIST' },
            ]
          : [{ type: 'Control', id: 'LIST' }],
    }),

    // CTL-06: fetch-all variant for callers that render the complete tenant
    // list (matrix, KPI dashboards, CSV export) rather than a paged table.
    // `getControls` alone silently truncates these at one page.
    getAllControls: builder.query({
      async queryFn(arg = {}, _queryApi, _extraOptions, baseQuery) {
        const { filters = {} } = arg;
        const result = await fetchAllControls(baseQuery, filters);
        if (result.error) {
          return { error: result.error };
        }
        return {
          data: {
            data: result.data,
            total: result.data.length,
            meta: result.meta,
          },
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Control', id })),
              { type: 'Control', id: 'LIST' },
            ]
          : [{ type: 'Control', id: 'LIST' }],
    }),

    getControlById: builder.query({
      query: (id) => ({
        url: `/api/v1/controls/${id}/`,
      }),
      providesTags: (result, error, id) => [{ type: 'Control', id }],
    }),

    createControl: builder.mutation({
      query: (data) => ({
        url: '/api/v1/controls/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Control', id: 'LIST' }, 'ControlStats'],
    }),

    updateControl: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/controls/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Control', id },
        { type: 'Control', id: 'LIST' },
        'ControlStats',
      ],
    }),

    deleteControl: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/controls/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: [{ type: 'Control', id: 'LIST' }, 'ControlStats'],
    }),

    // =========================================================================
    // Control Categories
    // =========================================================================
    getControlCategories: builder.query({
      query: () => ({
        url: '/api/v1/controls/categories/',
      }),
      transformResponse: (response) => response.results || response || [],
      providesTags: ['ControlCategory'],
    }),

    createControlCategory: builder.mutation({
      query: (data) => ({
        url: '/api/v1/controls/categories/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ControlCategory'],
    }),

    // =========================================================================
    // Control Tests
    // =========================================================================
    // =========================================================================
    // Control Evidence / Deficiencies / Self-Assessments (CTL-04)
    // =========================================================================
    // apps/control/views.py's Evidence/Deficiency/SelfAssessment ViewSets
    // filter on `?control_id=<id>` (verified in views.py get_queryset --
    // distinct from ControlAssignmentViewSet, which filters on `control`).
    getControlEvidence: builder.query({
      query: ({ controlId } = {}) => ({
        url: '/api/v1/controls/evidence/',
        params: controlId ? { control_id: controlId } : undefined,
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ControlEvidence', id })),
              { type: 'ControlEvidence', id: 'LIST' },
            ]
          : [{ type: 'ControlEvidence', id: 'LIST' }],
    }),

    // A chosen file goes as multipart (the browser sets the boundary); without one
    // the body stays JSON. Empty values are left out of the multipart body.
    createControlEvidence: builder.mutation({
      query: (data) => ({
        url: '/api/v1/controls/evidence/',
        method: 'POST',
        body: typeof File !== 'undefined' && data?.file instanceof File ? toFormData(data) : data,
      }),
      invalidatesTags: [{ type: 'ControlEvidence', id: 'LIST' }],
    }),

    // The file is streamed by the tenant-scoped download action (never a storage URL),
    // fetched as a blob with the auth header and saved locally.
    downloadControlEvidence: builder.mutation({
      async queryFn({ id, filename }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: `/api/v1/controls/evidence/${encodeURIComponent(String(id))}/download/`,
          responseHandler: async (response) => {
            if (response.ok) return response.blob();
            // a proxy error page is not JSON: never throw while reading the error body
            const text = await response.text();
            try {
              return JSON.parse(text);
            } catch {
              return { error: 'The file could not be downloaded.' };
            }
          },
        });
        if (result.error) return { error: result.error };
        const name =
          filenameFromDisposition(result.meta?.response?.headers?.get('content-disposition')) ||
          filename ||
          'evidence';
        saveBlob(result.data, name);
        return { data: { filename: name } };
      },
    }),

    getControlDeficiencies: builder.query({
      // affected_unit / owning_unit: report drill-down, each includes descendants.
      query: ({ controlId, affected_unit, owning_unit } = {}) => {
        const params = {
          ...(controlId ? { control_id: controlId } : {}),
          ...(affected_unit ? { affected_unit } : {}),
          ...(owning_unit ? { owning_unit } : {}),
        };
        return {
          url: '/api/v1/controls/deficiencies/',
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ControlDeficiency', id })),
              { type: 'ControlDeficiency', id: 'LIST' },
            ]
          : [{ type: 'ControlDeficiency', id: 'LIST' }],
    }),

    // apps/control/views.py::ControlDeficiencyViewSet.remediate reads
    // request.data.get('evidence', '') -- the payload key must be
    // `evidence`, not `remediation_evidence` (confirmed in views.py).
    remediateControlDeficiency: builder.mutation({
      query: ({ id, evidence }) => ({
        url: `/api/v1/controls/deficiencies/${id}/remediate/`,
        method: 'POST',
        body: { evidence },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ControlDeficiency', id },
        { type: 'ControlDeficiency', id: 'LIST' },
      ],
    }),

    updateControlDeficiency: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/controls/deficiencies/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ControlDeficiency', id },
        { type: 'ControlDeficiency', id: 'LIST' },
      ],
    }),

    closeControlDeficiency: builder.mutation({
      query: (id) => ({
        url: `/api/v1/controls/deficiencies/${id}/close/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ControlDeficiency', id },
        { type: 'ControlDeficiency', id: 'LIST' },
      ],
    }),

    getControlSelfAssessments: builder.query({
      query: ({ controlId } = {}) => ({
        url: '/api/v1/controls/self-assessments/',
        params: controlId ? { control_id: controlId } : undefined,
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ControlSelfAssessment', id })),
              { type: 'ControlSelfAssessment', id: 'LIST' },
            ]
          : [{ type: 'ControlSelfAssessment', id: 'LIST' }],
    }),

    // apps/control/models.py::ControlSelfAssessment.complete() writes
    // design_rating/operating_rating back onto the parent Control, so
    // completing a CSA also invalidates the Control detail cache entry
    // (controlId is passed through purely for that tag, not sent in body).
    completeControlSelfAssessment: builder.mutation({
      query: ({ id, controlId: _controlId, ...body }) => ({
        url: `/api/v1/controls/self-assessments/${id}/complete/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id, controlId }) => [
        { type: 'ControlSelfAssessment', id },
        { type: 'ControlSelfAssessment', id: 'LIST' },
        ...(controlId ? [{ type: 'Control', id: controlId }] : []),
      ],
    }),

    // =========================================================================
    // Statistics
    // =========================================================================
    getControlSummary: builder.query({
      query: () => ({
        url: '/api/v1/controls/summary/',
      }),
      providesTags: ['ControlStats'],
    }),

    getControlsByRisk: builder.query({
      query: (riskId) => ({
        url: `/api/v1/controls/?risk=${riskId}`,
      }),
      transformResponse: (response) => response.results || response || [],
    }),

    // =========================================================================
    // Control Assignments (department x control compliance matrix)
    // =========================================================================
    // CTL-06: a single page_size=500 request still silently truncates a
    // department x control matrix beyond 500 assignments. Page through the
    // full result set instead -- the matrix has no paged-table UI of its
    // own, so there is no "page 1 of N" affordance to fall back on.
    getControlAssignments: builder.query({
      async queryFn(arg = {}, _queryApi, _extraOptions, baseQuery) {
        const result = await fetchAllControlAssignments(baseQuery, arg);
        if (result.error) {
          return { error: result.error };
        }
        return {
          data: {
            data: result.data,
            total: result.data.length,
            meta: result.meta,
          },
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ControlAssignment', id })),
              { type: 'ControlAssignment', id: 'LIST' },
            ]
          : [{ type: 'ControlAssignment', id: 'LIST' }],
    }),

    getControlAssignment: builder.query({
      query: (id) => ({ url: `/api/v1/controls/assignments/${encodeURIComponent(id)}/` }),
      providesTags: (result, error, id) => [{ type: 'ControlAssignment', id }],
    }),

    createControlAssignment: builder.mutation({
      query: (data) => ({
        url: '/api/v1/controls/assignments/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'ControlAssignment', id: 'LIST' }],
    }),

    updateControlAssignment: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/controls/assignments/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ControlAssignment', id },
        { type: 'ControlAssignment', id: 'LIST' },
      ],
    }),

    // =========================================================================
    // Framework mappings (CTL-11)
    // =========================================================================
    // Tenant-specific links from a control to a framework requirement
    // (framework + clause_reference). The requirement catalogue comes from
    // master data (provider TaxonomyFrameworkMapping rows), see
    // apps/control/views.py::ControlFrameworkMappingViewSet.
    getControlFrameworkMappings: builder.query({
      query: ({ controlId } = {}) => ({
        url: '/api/v1/controls/framework-mappings/',
        params: { page_size: 200, ...(controlId ? { control: controlId } : {}) },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'ControlFrameworkMapping', id: 'LIST' }],
    }),

    createControlFrameworkMapping: builder.mutation({
      query: (data) => ({
        url: '/api/v1/controls/framework-mappings/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'ControlFrameworkMapping', id: 'LIST' }, 'FrameworkCoverage'],
    }),

    deleteControlFrameworkMapping: builder.mutation({
      query: (id) => ({
        url: `/api/v1/controls/framework-mappings/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ControlFrameworkMapping', id: 'LIST' }, 'FrameworkCoverage'],
    }),

    getFrameworkRequirementCatalogue: builder.query({
      query: () => ({ url: '/api/v1/controls/framework-mappings/catalogue/' }),
    }),

    getFrameworkCoverage: builder.query({
      query: () => ({ url: '/api/v1/controls/framework-mappings/coverage/' }),
      providesTags: ['FrameworkCoverage'],
    }),
  }),
});

export const {
  // Controls
  useGetControlsQuery,
  useGetAllControlsQuery,
  useGetControlByIdQuery,
  useCreateControlMutation,
  useUpdateControlMutation,
  useDeleteControlMutation,
  // Categories
  useGetControlCategoriesQuery,
  useCreateControlCategoryMutation,
  // Stats
  useGetControlSummaryQuery,
  useGetControlsByRiskQuery,
  // Assignments
  useGetControlAssignmentsQuery,
  useGetControlAssignmentQuery,
  useCreateControlAssignmentMutation,
  useUpdateControlAssignmentMutation,
  // Evidence/Deficiencies/Self-Assessments
  useGetControlEvidenceQuery,
  useCreateControlEvidenceMutation,
  useDownloadControlEvidenceMutation,
  useGetControlDeficienciesQuery,
  useRemediateControlDeficiencyMutation,
  useCloseControlDeficiencyMutation,
  useUpdateControlDeficiencyMutation,
  useGetControlSelfAssessmentsQuery,
  useCompleteControlSelfAssessmentMutation,
  // Framework mappings (CTL-11)
  useGetControlFrameworkMappingsQuery,
  useCreateControlFrameworkMappingMutation,
  useDeleteControlFrameworkMappingMutation,
  useGetFrameworkRequirementCatalogueQuery,
  useGetFrameworkCoverageQuery,
} = controlApi;

export default controlApi;
